import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import styled from "styled-components";
import TopNav from "./TopNav";
import { useFloorPlan, unscaleCoordinate } from "../hooks/useFloorPlan";
import { useRoomCleanUp } from "../hooks/useRoomCleanup";
import { useDispatch, useSelector } from "react-redux";
import {
  clearRedoStacks,
  pushUndoStacks,
  setCleanUpDone,
  setCleanUpRequired,
  setRooms,
  setWalls,
  setWindowAssets,
  setDoorAssets,
  setAssetType,
} from "../../../redux/features/appState";
import { WindowAsset, DoorAsset } from "../../../redux/features/appState/appState.model";
import { showToast } from "../../../redux/features/toast/toast.slice";
import { v4 as uuidv4 } from "uuid";
import { RootState } from "../../../redux/store";
import { Room, Wall } from "./Floorplan";
import { Box, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";

const CustomDiv = styled.div`
  .svgDualGraph {
    cursor: move;
    display: block;
    background: #FBE5C6;
    width: 100vw;
    height: 100vh;
    background-color: #FBE5C6;
  }
  .edge {
    stroke: black;
    stroke-width: 9px;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .drag-line {
    stroke: #000;
    stroke-width: 3px;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .highlighted {
    stroke: red;
    stroke-width: 4px;
  }
  .plot {
    stroke-width: 4px;
    stroke-linecap: round;
    stroke-dasharray: 10, 10;
  }
  .text {
    font-family: sans-serif;
    font-size: 10px;
    z-index: 1000;
  }
  .vertex {
    cursor: pointer;
  }
  .vertex:hover {
    stroke: #333;
    opacity: 0.8;
  }
  .label {
    text-anchor: middle;
    pointer-events: none;
    font-size: 14px;
    stroke: none;
  }
  .door {
    stroke: #FBE5C6;
    stroke-width: 5px;
    fill: #FBE5C6;
    outline: rgb(0, 0, 0);
  }
  .door-leaf,
  .door-arc {
    stroke: #000;
    stroke-width: 2px;
    fill: none;
    pointer-events: none;
  }
  .window {
    stroke: rgb(0, 0, 0);
    stroke-width: 2px;
    fill: rgb(0, 0, 0);
  }
  .door-group .resize-handle,
  .window-group .resize-handle {
    fill: #2563eb;
    stroke: #ffffff;
    stroke-width: 1.5px;
    opacity: 0;
    pointer-events: all;
    transition: opacity 0.15s ease-in-out, r 0.15s ease-in-out;
  }
  .door-group:hover .resize-handle,
  .window-group:hover .resize-handle {
    opacity: 1;
  }
  .door-group .resize-handle:hover,
  .window-group .resize-handle:hover {
    r: 7px;
  }
`;

interface GraphProps {
  fromRoomPlanner?: boolean;
}

const Graph: React.FC<GraphProps> = ({ fromRoomPlanner = false }) => {
  const [inputValue, setInputValue] = useState<string>("");

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const walls = useSelector(
    (state: RootState) => state.outputEditorButtonState.walls,
  );

  const rooms = useSelector(
    (state: RootState) => state.outputEditorButtonState.rooms,
  );

  const plot_boundary = useSelector(
    (state: RootState) => state.outputEditorButtonState.plot_boundary,
  );

  const draw = useSelector(
    (state: RootState) => state.outputEditorButtonState.drawLine,
  );
  const unit = useSelector(
    (state: RootState) => state.outputEditorButtonState.unit,
  );
  const floorplanHeight = useSelector(
    (state: RootState) => state.outputEditorButtonState.height,
  );
  const floorplanWidth = useSelector(
    (state: RootState) => state.outputEditorButtonState.width,
  );

  const windows = useSelector(
    (state: RootState) => state.outputEditorButtonState.drawWindow,
  );

  const doors = useSelector(
    (state: RootState) => state.outputEditorButtonState.drawDoor,
  );

  const windowAssets: WindowAsset[] | null | undefined = useSelector(
    (state: RootState) => state.outputEditorButtonState.windowAssets,
  );

  const doorAssets: DoorAsset[] | null | undefined = useSelector(
    (state: RootState) => state.outputEditorButtonState.doorAssets,
  );

  const fromDocuments = useSelector((state: RootState) => state.graph.fromDocuments);
  const location = useLocation();
  const navigate = useNavigate();
  const outputOriginPath = location.state?.originPath as string | undefined;
  let wall1: Wall | undefined, wall2: Wall | undefined;

  const svgRef = useRef<SVGSVGElement | null>(null);
  const contentGroupRef = useRef<SVGGElement | null>(null);
  const zoomTransform = useRef<d3.ZoomTransform>(d3.zoomIdentity);
  const doorAssetsRef = useRef(doorAssets);
  const windowAssetsRef = useRef(windowAssets);

  useEffect(() => {
    doorAssetsRef.current = doorAssets;
  }, [doorAssets]);

  useEffect(() => {
    windowAssetsRef.current = windowAssets;
  }, [windowAssets]);

  const dispatch = useDispatch();

  const pushAssetUndoSnapshot = (
    currentDoorAssets = doorAssetsRef.current || [],
    currentWindowAssets = windowAssetsRef.current || [],
  ) => {
    dispatch(
      pushUndoStacks({
        rooms,
        walls,
        plot: plot_boundary,
        doorAssets: currentDoorAssets,
        windowAssets: currentWindowAssets,
      }),
    );
    dispatch(clearRedoStacks());
  };

  useEffect(() => {
    dispatch(setCleanUpRequired(true));
  }, [dispatch]);

  useRoomCleanUp();

  useFloorPlan(svgRef, contentGroupRef, zoomTransform, fromRoomPlanner);

  const calculateUnscaledWallLength = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    width: number,
    height: number,
  ): number => {
    const unscaledX1 = unscaleCoordinate(x1, true, width, height);
    const unscaledY1 = unscaleCoordinate(y1, false, width, height);
    const unscaledX2 = unscaleCoordinate(x2, true, width, height);
    const unscaledY2 = unscaleCoordinate(y2, false, width, height);

    return Math.sqrt(
      Math.pow(unscaledX2 - unscaledX1, 2) +
        Math.pow(unscaledY2 - unscaledY1, 2),
    );
  };

  const drawLine = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    drag: boolean,
    contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    id: string,
    color: string,
  ) => {
    if (contentGroupRef.current)
      contentGroup = d3.select(contentGroupRef.current);

    if (drag) removeDragLine(contentGroup);

    const line = contentGroup
      .append("line")
      .attr("id", id)
      .attr("class", drag ? "drag-line" : "edge")
      .attr("x1", x1)
      .attr("y1", y1)
      .attr("x2", x2)
      .attr("y2", y2)
      .attr("stroke", "#000")
      .attr("stroke-width", 2);

    return line;
  };

  // Helper: find the wall an asset sits on
  const findAssetWall = (assetX: number, assetY: number, isVertical: boolean): Wall | undefined => {
    let bestWall: Wall | undefined;
    let minDist = Infinity;
    walls?.forEach((wall) => {
      if (isVertical && wall.x1 === wall.x2) {
        const dist = Math.abs(assetX - wall.x1);
        const minY = Math.min(wall.y1, wall.y2);
        const maxY = Math.max(wall.y1, wall.y2);
        if (dist < 15 && assetY >= minY - 5 && assetY <= maxY + 5) {
          const isCloser = dist < minDist;
          const isSameDistance = Math.abs(dist - minDist) < 0.5;
          const hasMoreRooms =
            isSameDistance &&
            (wall.roomIds?.length || 0) > (bestWall?.roomIds?.length || 0);
          if (isCloser || hasMoreRooms) {
            minDist = dist;
            bestWall = wall;
          }
        }
      } else if (!isVertical && wall.y1 === wall.y2) {
        const dist = Math.abs(assetY - wall.y1);
        const minX = Math.min(wall.x1, wall.x2);
        const maxX = Math.max(wall.x1, wall.x2);
        if (dist < 15 && assetX >= minX - 5 && assetX <= maxX + 5) {
          const isCloser = dist < minDist;
          const isSameDistance = Math.abs(dist - minDist) < 0.5;
          const hasMoreRooms =
            isSameDistance &&
            (wall.roomIds?.length || 0) > (bestWall?.roomIds?.length || 0);
          if (isCloser || hasMoreRooms) {
            minDist = dist;
            bestWall = wall;
          }
        }
      }
    });
    return bestWall;
  };

  const distanceToRoomBounds = (room: Room, px: number, py: number): number => {
    if (!room.walls || room.walls.length === 0) return Number.MAX_VALUE;

    const xs: number[] = [];
    const ys: number[] = [];
    room.walls.forEach((w) => {
      xs.push(w.x1, w.x2);
      ys.push(w.y1, w.y2);
    });

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const dx = px < minX ? minX - px : px > maxX ? px - maxX : 0;
    const dy = py < minY ? minY - py : py > maxY ? py - maxY : 0;

    return Math.hypot(dx, dy);
  };

  const getNearestRoomDistance = (px: number, py: number): number => {
    if (!rooms || rooms.length === 0) return Number.MAX_VALUE;
    let minDist = Number.MAX_VALUE;

    rooms.forEach((room) => {
      const dist = distanceToRoomBounds(room, px, py);
      if (dist < minDist) minDist = dist;
    });

    return minDist;
  };

  const getDoorSwingSign = (
    x: number,
    y: number,
    isVertical: boolean,
    wall?: Wall,
  ): 1 | -1 => {
    const targetWall = wall || findAssetWall(x, y, isVertical);
    if (!targetWall) return 1;

    const probeOffset = 18;
    const epsilon = 0.001;

    if (isVertical) {
      const leftDist = getNearestRoomDistance(targetWall.x1 - probeOffset, y);
      const rightDist = getNearestRoomDistance(targetWall.x1 + probeOffset, y);
      if (Math.abs(leftDist - rightDist) < epsilon) return 1;
      return leftDist < rightDist ? -1 : 1;
    }

    const upDist = getNearestRoomDistance(x, targetWall.y1 - probeOffset);
    const downDist = getNearestRoomDistance(x, targetWall.y1 + probeOffset);
    if (Math.abs(upDist - downDist) < epsilon) return 1;
    return upDist < downDist ? -1 : 1;
  };

  const getAssetLengthPx = (wall?: Wall, sizeRatio?: number): number => {
    if (!wall) return Math.max(8, 40); // fallback if no wall
    const wallPx = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);

    if (sizeRatio !== undefined) {
      const boundedRatio = Math.max(0.1, Math.min(0.5, sizeRatio));
      return wallPx * boundedRatio;
    }

    const targetUnits = unit === "meters" ? 0.6096 : 2; // 2 feet
    // Calculate unscaled wall length using the same approach as useFloorPlan.ts
    const fpWidth = floorplanWidth || 1;
    const fpHeight = floorplanHeight || 1;
    const unscaledX1 = unscaleCoordinate(wall.x1, true, fpWidth, fpHeight);
    const unscaledY1 = unscaleCoordinate(wall.y1, false, fpWidth, fpHeight);
    const unscaledX2 = unscaleCoordinate(wall.x2, true, fpWidth, fpHeight);
    const unscaledY2 = unscaleCoordinate(wall.y2, false, fpWidth, fpHeight);
    const wallUnits = Math.hypot(unscaledX2 - unscaledX1, unscaledY2 - unscaledY1);

    let defaultPx = Math.max(8, wallPx * 0.25);
    if (wallUnits && Number.isFinite(wallUnits) && wallUnits > 0) {
      const pxPerUnit = wallPx / wallUnits;
      defaultPx = targetUnits * pxPerUnit;
    }
    // Make sure it is within [10%, 50%] of wallPx
    return Math.max(wallPx * 0.1, Math.min(wallPx * 0.5, defaultPx));
  };

  const drawDoorGeometry = (
    group: d3.Selection<SVGGElement, unknown, null, undefined>,
    id: string,
    x: number,
    y: number,
    vertical: boolean,
    horizontal: boolean,
    swingSign: 1 | -1,
    assetLengthPx: number,
  ) => {
    const halfLen = assetLengthPx / 2;
    const x1: number = vertical ? x : x - halfLen;
    const x2: number = vertical ? x : x + halfLen;
    const y1: number = horizontal ? y : y - halfLen;
    const y2: number = horizontal ? y : y + halfLen;

    group
      .append("line")
      .attr("id", id)
      .attr("class", "door")
      .attr("x1", x1)
      .attr("y1", y1)
      .attr("x2", x2)
      .attr("y2", y2);

    const hingeX = vertical ? x : x - halfLen;
    const hingeY = vertical ? y - halfLen : y;
    const closedX = vertical ? x : x + halfLen;
    const closedY = vertical ? y + halfLen : y;
    const openX = vertical ? x + swingSign * assetLengthPx : x - halfLen;
    const openY = vertical ? y - halfLen : y + swingSign * assetLengthPx;

    const closedAngle = Math.atan2(closedY - hingeY, closedX - hingeX);
    const openAngle = Math.atan2(openY - hingeY, openX - hingeX);
    const cross =
      (closedX - hingeX) * (openY - hingeY) -
      (closedY - hingeY) * (openX - hingeX);
    const arcPath = d3.path();

    // Use hinge-centered arcs so left/right wall doors always render a clean quarter swing.
    arcPath.arc(
      hingeX,
      hingeY,
      assetLengthPx,
      closedAngle,
      openAngle,
      cross < 0,
    );

    group
      .append("line")
      .attr("class", "door-leaf")
      .attr("x1", hingeX)
      .attr("y1", hingeY)
      .attr("x2", openX)
      .attr("y2", openY);

    group
      .append("path")
      .attr("class", "door-arc")
      .attr("d", arcPath.toString());
  };

  // Helper: attach wall-constrained drag to an asset group
  const makeAssetDraggable = (
    group: d3.Selection<SVGGElement, unknown, null, undefined>,
    assetId: string,
    origX: number,
    origY: number,
    isVertical: boolean,
    assetType: "door" | "window",
  ) => {
    let currentX = origX;
    let currentY = origY;
    let dragStartX = origX;
    let dragStartY = origY;
    let wall: Wall | undefined;
    let halfLen = 20;
    let doorAssetsBeforeDrag = doorAssetsRef.current || [];
    let windowAssetsBeforeDrag = windowAssetsRef.current || [];

    const drag = d3
      .drag<SVGGElement, unknown>()
      .on("start", function (event) {
        dragStartX = currentX;
        dragStartY = currentY;
        doorAssetsBeforeDrag = doorAssetsRef.current || [];
        windowAssetsBeforeDrag = windowAssetsRef.current || [];
        wall = findAssetWall(currentX, currentY, isVertical);
        const asset = assetType === "door"
          ? doorAssetsBeforeDrag.find((a) => a.id === assetId)
          : windowAssetsBeforeDrag.find((a) => a.id === assetId);
        halfLen = getAssetLengthPx(wall, asset?.sizeRatio) / 2;
        d3.select(this).raise().style("cursor", "grabbing");
        event.sourceEvent.stopPropagation();
      })
      .on("drag", function (event) {
        if (!wall) return;
        if (isVertical) {
          const minY = Math.min(wall.y1, wall.y2) + halfLen;
          const maxY = Math.max(wall.y1, wall.y2) - halfLen;
          currentY = Math.max(minY, Math.min(maxY, currentY + event.dy));
          d3.select(this).attr("transform", `translate(0, ${currentY - origY})`);
        } else {
          const minX = Math.min(wall.x1, wall.x2) + halfLen;
          const maxX = Math.max(wall.x1, wall.x2) - halfLen;
          currentX = Math.max(minX, Math.min(maxX, currentX + event.dx));
          d3.select(this).attr("transform", `translate(${currentX - origX}, 0)`);
        }
      })
      .on("end", function () {
        d3.select(this).style("cursor", "grab");
        const moved =
          Math.abs(currentX - dragStartX) > 0.001 ||
          Math.abs(currentY - dragStartY) > 0.001;

        if (!moved) return;

        if (assetType === "door") {
          const swingSign = getDoorSwingSign(currentX, currentY, isVertical, wall);
          const updated = doorAssetsBeforeDrag.map((a) =>
            a.id === assetId ? { ...a, x: currentX, y: currentY, swingSign } : a,
          );
          pushAssetUndoSnapshot(doorAssetsBeforeDrag, windowAssetsBeforeDrag);
          dispatch(setDoorAssets(updated));
        } else {
          const updated = windowAssetsBeforeDrag.map((a) =>
            a.id === assetId ? { ...a, x: currentX, y: currentY } : a,
          );
          pushAssetUndoSnapshot(doorAssetsBeforeDrag, windowAssetsBeforeDrag);
          dispatch(setWindowAssets(updated));
        }
      });

    group.call(drag).style("cursor", "grab");
  };

  const drawDoor = (
    x: number,
    y: number,
    vertical: boolean,
    horizontal: boolean,
    contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    id: string,
  ) => {
    if (contentGroupRef.current)
      contentGroup = d3.select(contentGroupRef.current);

    const wall = findAssetWall(x, y, vertical);
    if (wall) {
      const wallLen = calculateUnscaledWallLength(
        wall.x1,
        wall.y1,
        wall.x2,
        wall.y2,
        floorplanWidth,
        floorplanHeight,
      );
      if (wallLen < 2) {
        dispatch(
          showToast({
            message: "Wall Length must be atleast 2 feet for asset addition.",
            type: "error",
          }),
        );
        dispatch(setAssetType(null));
        return;
      }

      const halfLen = getAssetLengthPx(wall) / 2;
      if (vertical) {
        const minY = Math.min(wall.y1, wall.y2) + halfLen;
        const maxY = Math.max(wall.y1, wall.y2) - halfLen;
        y = Math.max(minY, Math.min(maxY, y));
        x = wall.x1;
      } else {
        const minX = Math.min(wall.x1, wall.x2) + halfLen;
        const maxX = Math.max(wall.x1, wall.x2) - halfLen;
        x = Math.max(minX, Math.min(maxX, x));
        y = wall.y1;
      }
    }

    const group = contentGroup
      .append("g")
      .attr("class", "door-group") as d3.Selection<SVGGElement, unknown, null, undefined>;

    const swingSign = getDoorSwingSign(x, y, vertical);
    drawDoorGeometry(
      group,
      id,
      x,
      y,
      vertical,
      horizontal,
      swingSign,
      getAssetLengthPx(wall),
    );

    const doorData = {
      id,
      x,
      y,
      isVertical: vertical,
      swingSign,
      sizeRatio: wall ? (getAssetLengthPx(wall) / Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1)) : undefined,
    };

    pushAssetUndoSnapshot();
    dispatch(setDoorAssets([...(doorAssets || []), doorData]));

    makeAssetDraggable(group, id, x, y, vertical, "door");

    return group;
  };

  const drawWindow = (
    x: number,
    y: number,
    vertical: boolean,
    horizontal: boolean,
    contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    id: string,
  ) => {
    if (contentGroupRef.current)
      contentGroup = d3.select(contentGroupRef.current);

    const wall = findAssetWall(x, y, vertical);
    if (wall) {
      const wallLen = calculateUnscaledWallLength(
        wall.x1,
        wall.y1,
        wall.x2,
        wall.y2,
        floorplanWidth,
        floorplanHeight,
      );
      if (wallLen < 2) {
        dispatch(
          showToast({
            message: "Wall Length must be atleast 2 feet for asset addition.",
            type: "error",
          }),
        );
        dispatch(setAssetType(null));
        return;
      }
    }
    const windowLength = getAssetLengthPx(wall);
    if (wall) {
      const halfLen = windowLength / 2;
      if (vertical) {
        const minY = Math.min(wall.y1, wall.y2) + halfLen;
        const maxY = Math.max(wall.y1, wall.y2) - halfLen;
        y = Math.max(minY, Math.min(maxY, y));
        x = wall.x1;
      } else {
        const minX = Math.min(wall.x1, wall.x2) + halfLen;
        const maxX = Math.max(wall.x1, wall.x2) - halfLen;
        x = Math.max(minX, Math.min(maxX, x));
        y = wall.y1;
      }
    }

    const offset = 2;

    let x1, y1, x2, y2;

    if (vertical) {
      x1 = x;
      y1 = y - windowLength / 2;
      x2 = x;
      y2 = y + windowLength / 2;
    } else {
      x1 = x - windowLength / 2;
      y1 = y;
      x2 = x + windowLength / 2;
      y2 = y;
    }

    const group = contentGroup
      .append("g")
      .attr("class", "window-group") as d3.Selection<SVGGElement, unknown, null, undefined>;

    group
      .append("line")
      .attr("id", id)
      .attr("class", "window")
      .attr("x1", x1)
      .attr("y1", y1)
      .attr("x2", x2)
      .attr("y2", y2);

    if (vertical) {
      group
        .append("line")
        .attr("class", "window-side-line")
        .attr("x1", x1 - offset)
        .attr("y1", y1)
        .attr("x2", x1 - offset)
        .attr("y2", y2)
        .attr("stroke", "#FBE5C6")
        .attr("stroke-width", 2);

      group
        .append("line")
        .attr("class", "window-side-line")
        .attr("x1", x1 + offset)
        .attr("y1", y1)
        .attr("x2", x1 + offset)
        .attr("y2", y2)
        .attr("stroke", "#FBE5C6")
        .attr("stroke-width", 2);
    } else {
      group
        .append("line")
        .attr("class", "window-side-line")
        .attr("x1", x1)
        .attr("y1", y1 - offset)
        .attr("x2", x2)
        .attr("y2", y1 - offset)
        .attr("stroke", "#FBE5C6")
        .attr("stroke-width", 2);

      group
        .append("line")
        .attr("class", "window-side-line")
        .attr("x1", x1)
        .attr("y1", y1 + offset)
        .attr("x2", x2)
        .attr("y2", y1 + offset)
        .attr("stroke", "#FBE5C6")
        .attr("stroke-width", 2);
    }

    const windowData = {
      id,
      x,
      y,
      isVertical: vertical,
      sizeRatio: wall ? (windowLength / Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1)) : undefined,
    };

    pushAssetUndoSnapshot();
    dispatch(setWindowAssets([...(windowAssets || []), windowData]));

    makeAssetDraggable(group, id, x, y, vertical, "window");

    return group;
  };

  // Redraws assets from Redux state (without dispatching again)
  const attachResizeHandles = (
    group: d3.Selection<SVGGElement, unknown, null, undefined>,
    assetId: string,
    x: number,
    y: number,
    isVertical: boolean,
    assetType: "door" | "window",
    wall?: Wall,
  ) => {
    if (!wall) return;
    const wallPx = Math.hypot(wall.x2 - wall.x1, wall.y2 - wall.y1);
    
    // Resolve current sizeRatio and length
    const getCurAsset = () => {
      return assetType === "door"
        ? (doorAssetsRef.current || []).find((a: any) => a.id === assetId)
        : (windowAssetsRef.current || []).find((a: any) => a.id === assetId);
    };
    
    const asset = getCurAsset();
    const lengthPx = getAssetLengthPx(wall, asset?.sizeRatio);
    const halfLen = lengthPx / 2;

    const handle1 = group
      .append("circle")
      .attr("class", "resize-handle handle-1")
      .attr("r", 5)
      .style("cursor", isVertical ? "ns-resize" : "ew-resize");

    const handle2 = group
      .append("circle")
      .attr("class", "resize-handle handle-2")
      .attr("r", 5)
      .style("cursor", isVertical ? "ns-resize" : "ew-resize");

    if (isVertical) {
      handle1.attr("cx", x).attr("cy", y - halfLen);
      handle2.attr("cx", x).attr("cy", y + halfLen);
    } else {
      handle1.attr("cx", x - halfLen).attr("cy", y);
      handle2.attr("cx", x + halfLen).attr("cy", y);
    }

    let doorAssetsBeforeResize: any[] = [];
    let windowAssetsBeforeResize: any[] = [];
    
    const resizeDrag = d3
      .drag<SVGCircleElement, unknown>()
      .on("start", function (event) {
        event.sourceEvent.stopPropagation();
        doorAssetsBeforeResize = doorAssetsRef.current || [];
        windowAssetsBeforeResize = windowAssetsRef.current || [];
      })
      .on("drag", function (event) {
        event.sourceEvent.stopPropagation();
        const pointer = d3.pointer(event.sourceEvent || event, svgRef.current);
        const pointerX = pointer[0];
        const pointerY = pointer[1];
        
        let newHalfLen = 20;
        if (isVertical) {
          newHalfLen = Math.abs(pointerY - y);
        } else {
          newHalfLen = Math.abs(pointerX - x);
        }

        const newLength = 2 * newHalfLen;
        const minPx = wallPx * 0.1;
        const maxPx = wallPx * 0.5;
        const clampedLength = Math.max(minPx, Math.min(maxPx, newLength));
        const clampedHalfLen = clampedLength / 2;

        if (assetType === "door") {
          const curA = getCurAsset() as any;
          const swingSign = curA?.swingSign || 1;
          if (isVertical) {
            group.select(".door")
              .attr("y1", y - clampedHalfLen)
              .attr("y2", y + clampedHalfLen);
            
            const hingeY = y - clampedHalfLen;
            const closedY = y + clampedHalfLen;
            const openY = hingeY;
            const openX = x + swingSign * clampedLength;

            group.select(".door-leaf")
              .attr("y1", hingeY)
              .attr("x2", openX)
              .attr("y2", openY);

            const closedAngle = Math.atan2(closedY - hingeY, 0);
            const openAngle = Math.atan2(openY - hingeY, openX - x);
            const cross = (x - x) * (openY - hingeY) - (closedY - hingeY) * (openX - x);
            const arcPath = d3.path();
            arcPath.arc(x, hingeY, clampedLength, closedAngle, openAngle, cross < 0);
            group.select(".door-arc").attr("d", arcPath.toString());
          } else {
            group.select(".door")
              .attr("x1", x - clampedHalfLen)
              .attr("x2", x + clampedHalfLen);

            const hingeX = x - clampedHalfLen;
            const closedX = x + clampedHalfLen;
            const openX = hingeX;
            const openY = y + swingSign * clampedLength;

            group.select(".door-leaf")
              .attr("x1", hingeX)
              .attr("x2", openX)
              .attr("y2", openY);

            const closedAngle = Math.atan2(0, closedX - hingeX);
            const openAngle = Math.atan2(openY - y, openX - hingeX);
            const cross = (closedX - hingeX) * (openY - y) - (y - y) * (openX - hingeX);
            const arcPath = d3.path();
            arcPath.arc(hingeX, y, clampedLength, closedAngle, openAngle, cross < 0);
            group.select(".door-arc").attr("d", arcPath.toString());
          }
        } else {
          if (isVertical) {
            group.select(".window")
              .attr("y1", y - clampedHalfLen)
              .attr("y2", y + clampedHalfLen);
            
            group.selectAll(".window-side-line")
              .attr("y1", y - clampedHalfLen)
              .attr("y2", y + clampedHalfLen);
          } else {
            group.select(".window")
              .attr("x1", x - clampedHalfLen)
              .attr("x2", x + clampedHalfLen);

            group.selectAll(".window-side-line")
              .attr("x1", x - clampedHalfLen)
              .attr("x2", x + clampedHalfLen);
          }
        }

        if (isVertical) {
          group.select(".handle-1").attr("cy", y - clampedHalfLen);
          group.select(".handle-2").attr("cy", y + clampedHalfLen);
        } else {
          group.select(".handle-1").attr("cx", x - clampedHalfLen);
          group.select(".handle-2").attr("cx", x + clampedHalfLen);
        }
      })
      .on("end", function (event) {
        event.sourceEvent.stopPropagation();
        const pointer = d3.pointer(event.sourceEvent || event, svgRef.current);
        const pointerX = pointer[0];
        const pointerY = pointer[1];
        
        let finalHalfLen = 20;
        if (isVertical) {
          finalHalfLen = Math.abs(pointerY - y);
        } else {
          finalHalfLen = Math.abs(pointerX - x);
        }
        const finalLength = 2 * finalHalfLen;
        const minPx = wallPx * 0.1;
        const maxPx = wallPx * 0.5;
        const clampedLength = Math.max(minPx, Math.min(maxPx, finalLength));
        const finalRatio = clampedLength / wallPx;

        if (assetType === "door") {
          const updated = doorAssetsBeforeResize.map((a: any) =>
            a.id === assetId ? { ...a, sizeRatio: finalRatio } : a
          );
          pushAssetUndoSnapshot(doorAssetsBeforeResize, windowAssetsBeforeResize);
          dispatch(setDoorAssets(updated));
        } else {
          const updated = windowAssetsBeforeResize.map((a: any) =>
            a.id === assetId ? { ...a, sizeRatio: finalRatio } : a
          );
          pushAssetUndoSnapshot(doorAssetsBeforeResize, windowAssetsBeforeResize);
          dispatch(setWindowAssets(updated));
        }
      });

    handle1.call(resizeDrag);
    handle2.call(resizeDrag);
  };

  // Redraws assets from Redux state (without dispatching again)
  const redrawDoorNoDipatch = (
    x: number,
    y: number,
    vertical: boolean,
    horizontal: boolean,
    contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    id: string,
    swingSign?: 1 | -1,
    sizeRatio?: number,
  ) => {
    if (contentGroupRef.current)
      contentGroup = d3.select(contentGroupRef.current);

    const group = contentGroup
      .append("g")
      .attr("class", "door-group") as d3.Selection<SVGGElement, unknown, null, undefined>;

    const wall = findAssetWall(x, y, vertical);
    const resolvedSwingSign = swingSign || getDoorSwingSign(x, y, vertical);
    drawDoorGeometry(
      group,
      id,
      x,
      y,
      vertical,
      horizontal,
      resolvedSwingSign,
      getAssetLengthPx(wall, sizeRatio),
    );

    makeAssetDraggable(group, id, x, y, vertical, "door");
    attachResizeHandles(group, id, x, y, vertical, "door", wall);
  };

  const redrawWindowNoDispatch = (
    x: number,
    y: number,
    vertical: boolean,
    horizontal: boolean,
    contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
    id: string,
    sizeRatio?: number,
  ) => {
    if (contentGroupRef.current)
      contentGroup = d3.select(contentGroupRef.current);

    const wall = findAssetWall(x, y, vertical);
    const windowLength = getAssetLengthPx(wall, sizeRatio);
    const offset = 2;
    let x1, y1, x2, y2;

    if (vertical) {
      x1 = x; y1 = y - windowLength / 2; x2 = x; y2 = y + windowLength / 2;
    } else {
      x1 = x - windowLength / 2; y1 = y; x2 = x + windowLength / 2; y2 = y;
    }

    const group = contentGroup
      .append("g")
      .attr("class", "window-group") as d3.Selection<SVGGElement, unknown, null, undefined>;

    group.append("line").attr("id", id).attr("class", "window")
      .attr("x1", x1).attr("y1", y1).attr("x2", x2).attr("y2", y2);

    if (vertical) {
      group.append("line").attr("class", "window-side-line")
        .attr("x1", x1 - offset).attr("y1", y1).attr("x2", x1 - offset).attr("y2", y2)
        .attr("stroke", "#FBE5C6").attr("stroke-width", 2);
      group.append("line").attr("class", "window-side-line")
        .attr("x1", x1 + offset).attr("y1", y1).attr("x2", x1 + offset).attr("y2", y2)
        .attr("stroke", "#FBE5C6").attr("stroke-width", 2);
    } else {
      group.append("line").attr("class", "window-side-line")
        .attr("x1", x1).attr("y1", y1 - offset).attr("x2", x2).attr("y2", y1 - offset)
        .attr("stroke", "#FBE5C6").attr("stroke-width", 2);
      group.append("line").attr("class", "window-side-line")
        .attr("x1", x1).attr("y1", y1 + offset).attr("x2", x2).attr("y2", y1 + offset)
        .attr("stroke", "#FBE5C6").attr("stroke-width", 2);
    }

    makeAssetDraggable(group, id, x, y, vertical, "window");
    attachResizeHandles(group, id, x, y, vertical, "window", wall);
  };

  const redrawAssets = () => {
    if (contentGroupRef.current) {
      const contentGroup = d3.select(contentGroupRef.current);

      contentGroup.selectAll(".window-group").remove();
      contentGroup.selectAll(".window").remove();
      contentGroup.selectAll(".door-group").remove();
      contentGroup.selectAll(".door").remove();

      windowAssets?.forEach((w) => {
        redrawWindowNoDispatch(
          w.x, w.y, w.isVertical, !w.isVertical, contentGroup, w.id, w.sizeRatio,
        );
      });

      doorAssets?.forEach((d) => {
        redrawDoorNoDipatch(
          d.x, d.y, d.isVertical, !d.isVertical, contentGroup, d.id, d.swingSign, d.sizeRatio,
        );
      });
    }
  };

  useEffect(() => {
    redrawAssets();
  }, [windows, doors]);

  useEffect(() => {
    redrawAssets();
  }, [doorAssets, windowAssets]);

  let dragging: boolean = false;
  let dragStartNode: { id: string; x: number; y: number } | null = null;

  let limits: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    minXWall: Wall | undefined;
    maxXWall: Wall | undefined;
    minYWall: Wall | undefined;
    maxYWall: Wall | undefined;
  };

  let contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>;

  const removeDragLine = (
    contentGroup: d3.Selection<SVGGElement, unknown, null, undefined>,
  ) => {
    contentGroup?.select(".drag-line").attr("class", "drag-line").remove();
  };

  const newWallLimits = (dragStartNode: {
    id: string;
    x: number;
    y: number;
  }) => {
    let minX: number = Number.MIN_VALUE,
      maxX: number = Number.MAX_VALUE,
      minY: number = Number.MIN_VALUE,
      maxY: number = Number.MAX_VALUE;

    let minXWall: Wall | undefined,
      maxXWall: Wall | undefined,
      minYWall: Wall | undefined,
      maxYWall: Wall | undefined;

    walls?.forEach((wall) => {
      let isHorizontal: boolean = wall.y1 === wall.y2;

      if (isHorizontal) {
        if (
          Math.abs(wall.x1 - dragStartNode.x) +
          Math.abs(wall.x2 - dragStartNode.x) ===
          Math.abs(wall.x1 - wall.x2)
        ) {
          if (wall.y1 > dragStartNode.y) {
            if (maxY > wall.y1) {
              maxY = wall.y1;
              maxYWall = wall;
            }
          } else if (wall.y1 < dragStartNode.y) {
            if (minY < wall.y1) {
              minY = wall.y1;
              minYWall = wall;
            }
          }
        }
      } else {
        if (
          Math.abs(wall.y1 - dragStartNode.y) +
          Math.abs(wall.y2 - dragStartNode.y) ===
          Math.abs(wall.y1 - wall.y2)
        ) {
          if (wall.x1 > dragStartNode.x) {
            if (maxX > wall.x1) {
              maxX = wall.x1;
              maxXWall = wall;
            }
          } else if (wall.x1 < dragStartNode.x) {
            if (minX < wall.x1) {
              minX = wall.x1;
              minXWall = wall;
            }
          }
        }
      }
    });

    return { minX, maxX, minY, maxY, minXWall, maxXWall, minYWall, maxYWall };
  };

  const nearestWall = (x: number, y: number) => {
    let minDist: number = Number.MAX_VALUE;
    let minDistWall: Wall | undefined;

    walls?.forEach((wall) => {
      let dist: number = Number.MAX_VALUE;

      if (wall.x1 === wall.x2 && (!wall1 || (wall1 && wall1.x1 === wall1.x2))) {
        dist = Math.abs(x - wall.x1);
      } else if (
        wall.y1 === wall.y2 &&
        (!wall1 || (wall1 && wall1.y1 === wall1.y2))
      ) {
        dist = Math.abs(y - wall.y1);
      }

      if (dist < 5) {
        // Prefer common walls (shared between more rooms) when distances are equal or very close
        const isCloser = dist < minDist;
        const isSameDistance = Math.abs(dist - minDist) < 0.5;
        const hasMoreRooms =
          isSameDistance &&
          (wall.roomIds?.length || 0) > (minDistWall?.roomIds?.length || 0);

        if (isCloser || hasMoreRooms) {
          minDist = dist;
          minDistWall = wall;
        }
      }
    });

    return minDistWall;
  };

  const hasNonEmptyArray = (value: any) => Array.isArray(value) && value.length > 0;

  const hasNonEmptyObject = (value: any) =>
    value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;

  const hasMeaningfulNewInputState = (value: any) => {
    if (!value || typeof value !== "object") return false;

    if (hasNonEmptyArray(value.rooms)) return true;
    if (hasNonEmptyArray(value.fixedRooms)) return true;
    if (hasNonEmptyArray(value.adjacencyPairs)) return true;
    if (hasNonEmptyArray(value.nonAdjacencyPairs)) return true;
    if (hasNonEmptyArray(value.canvasLines)) return true;
    if (hasNonEmptyArray(value.floorplans)) return true;
    if (hasNonEmptyArray(value.apiBoundary)) return true;
    if (hasNonEmptyObject(value.idToNameMap)) return true;
    if (value.spaceResult || value.gaResult) return true;

    return false;
  };

  const navigateToInput = () => {
    if (outputOriginPath) {
      navigate(outputOriginPath);
      return;
    }

    if (fromDocuments) {
      navigate("/saved-documents");
      return;
    }

    // Backward compatibility for older flows that don't pass origin state.
    const rawNewInputState = sessionStorage.getItem("newInputEditorState");
    if (rawNewInputState) {
      try {
        const parsedNewInputState = JSON.parse(rawNewInputState);
        if (hasMeaningfulNewInputState(parsedNewInputState)) {
          navigate("/newinputeditor");
          return;
        }
      } catch (error) {
        console.warn("Failed to parse new input editor session state:", error);
      }
    }

    navigate("/inputeditor");
  };

  const getRoom = (newWall: Wall) => {
    const isNewWallHorizontal: boolean = newWall.y1 === newWall.y2;

    if (rooms)
      for (let room of rooms) {
        let isLeft: boolean = false,
          isRight: boolean = false,
          isAbove: boolean = false,
          isBelow: boolean = false;

        room?.walls?.forEach((wall) => {
          let isWallHorizontal: boolean = wall.y1 === wall.y2;
          let overlap: boolean = false;

          if (
            isNewWallHorizontal &&
            isWallHorizontal &&
            Math.max(newWall.x1, newWall.x2, wall.x1, wall.x2) -
            Math.min(newWall.x1, newWall.x2, wall.x1, wall.x2) <=
            Math.abs(newWall.x1 - newWall.x2) + Math.abs(wall.x1 - wall.x2)
          )
            overlap = true;
          else if (
            !isNewWallHorizontal &&
            !isWallHorizontal &&
            Math.max(newWall.y1, newWall.y2, wall.y1, wall.y2) -
            Math.min(newWall.y1, newWall.y2, wall.y1, wall.y2) <=
            Math.abs(newWall.y1 - newWall.y2) + Math.abs(wall.y1 - wall.y2)
          )
            overlap = true;
          else if (
            isNewWallHorizontal &&
            !isWallHorizontal &&
            Math.abs(newWall.y1 - wall.y1) + Math.abs(newWall.y1 - wall.y2) ===
            Math.abs(wall.y1 - wall.y2)
          )
            overlap = true;
          else if (
            !isNewWallHorizontal &&
            isWallHorizontal &&
            Math.abs(newWall.x1 - wall.x1) + Math.abs(newWall.x1 - wall.x2) ===
            Math.abs(wall.x1 - wall.x2)
          )
            overlap = true;

          if (overlap) {
            if (Math.max(newWall.x1, newWall.x2) <= Math.min(wall.x1, wall.x2))
              isLeft = true;
            else if (
              Math.min(newWall.x1, newWall.x2) >= Math.max(wall.x1, wall.x2)
            )
              isRight = true;

            if (Math.max(newWall.y1, newWall.y2) <= Math.min(wall.y1, wall.y2))
              isAbove = true;
            else if (
              Math.min(newWall.y1, newWall.y2) >= Math.max(wall.y1, wall.y2)
            )
              isBelow = true;
          }
        });

        if (isLeft && isRight && isAbove && isBelow) return room.id;
      }
  };

  const handleClick = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if (draw) {
      if (!dragging) {
        let [x, y] = d3.pointer(event, svgRef.current);
        dragging = true;

        wall1 = nearestWall(x, y);

        if (wall1) {
          if (wall1.x1 === wall1.x2) x = wall1.x1;
          else y = wall1.y1;
        }

        dragStartNode = { id: uuidv4(), x, y };
        limits = newWallLimits(dragStartNode);

        if (contentGroupRef.current) {
          contentGroup = d3.select(contentGroupRef.current);
        }

        drawLine(x, y, x, y, true, contentGroup, dragStartNode.id, "#000");
      } else if (dragging && dragStartNode) {
        let [x, y] = d3.pointer(event, svgRef.current);

        wall2 = nearestWall(x, y);

        if (wall1 && wall2) {
          if (wall1.x1 === wall1.x2) {
            x = wall2.x1;
            y = dragStartNode.y;
          } else {
            x = dragStartNode.x;
            y = wall2.y1;
          }
        } else {
          if (Math.abs(dragStartNode.x - x) >= Math.abs(dragStartNode.y - y))
            y = dragStartNode.y;
          else x = dragStartNode.x;
        }

        x = Math.max(Math.min(x, limits.maxX), limits.minX);
        y = Math.max(Math.min(y, limits.maxY), limits.minY);

        let line = drawLine(
          dragStartNode.x,
          dragStartNode.y,
          x,
          y,
          false,
          contentGroup,
          dragStartNode.id,
          "#000",
        );

        dragging = false;
        dragStartNode = null;

        let wall: Wall = {
          id: line.attr("id"),
          x1: parseFloat(line.attr("x1")),
          x2: parseFloat(line.attr("x2")),
          y1: parseFloat(line.attr("y1")),
          y2: parseFloat(line.attr("y2")),
          roomIds: [],
        };

        let roomid = getRoom(wall);

        let updatedWalls: Wall[] | undefined = [];
        if (walls) {
          updatedWalls = [...walls];
        }

        let updatedRooms: Room[] | undefined = rooms?.filter(
          (r) => r.id !== roomid,
        );

        if (roomid) {
          wall.roomIds.push(roomid);
          updatedWalls?.push(wall);

          let room: Room | undefined = rooms?.find((r) => r.id === roomid);
          if (room) {
            let updatedRoomWalls: Wall[] | undefined = room.walls;
            if (updatedRoomWalls) updatedRoomWalls = [...updatedRoomWalls, wall];
            room = { ...room, walls: updatedRoomWalls };
            updatedRooms?.push(room);
          }
        }

        dispatch(clearRedoStacks());
        dispatch(
          pushUndoStacks({ rooms: rooms, walls: walls, plot: plot_boundary }),
        );

        if (updatedWalls) dispatch(setWalls(updatedWalls));
        if (updatedRooms) dispatch(setRooms(updatedRooms));

        dispatch(setCleanUpDone(true));

        wall1 = undefined;
        wall2 = undefined;
      }
    } else if (windows) {
      let [x, y] = d3.pointer(event, svgRef.current);
      wall1 = nearestWall(x, y);
      dragStartNode = { id: uuidv4(), x, y };
      if (wall1) {
        let isVertical = false;
        let isHorizontal = false;

        if (wall1.x1 === wall1.x2) {
          x = wall1.x1;
          isVertical = true;
          drawWindow(x, y, isVertical, isHorizontal, contentGroup, dragStartNode.id);
        } else {
          y = wall1.y1;
          isHorizontal = true;
          drawWindow(x, y, isVertical, isHorizontal, contentGroup, dragStartNode.id);
        }
      }
    } else if (doors) {
      let [x, y] = d3.pointer(event, svgRef.current);
      wall1 = nearestWall(x, y);
      dragStartNode = { id: uuidv4(), x, y };
      if (wall1) {
        let isVertical = false;
        let isHorizontal = false;

        if (wall1.x1 === wall1.x2) {
          x = wall1.x1;
          isVertical = true;
          drawDoor(x, y, isVertical, isHorizontal, contentGroup, dragStartNode.id);
        } else {
          y = wall1.y1;
          isHorizontal = true;
          drawDoor(x, y, isVertical, isHorizontal, contentGroup, dragStartNode.id);
        }
      }
    }
  };

  const handleMouseMove = (
    event: React.MouseEvent<SVGSVGElement, MouseEvent>,
  ) => {
    event.preventDefault();

    if (draw) {
      if (dragging && dragStartNode) {
        let [x, y] = d3.pointer(event, svgRef.current);

        if (wall1) {
          if (wall1.x1 === wall1.x2) y = dragStartNode.y;
          else x = dragStartNode.x;
        } else {
          if (Math.abs(dragStartNode.x - x) >= Math.abs(dragStartNode.y - y))
            y = dragStartNode.y;
          else x = dragStartNode.x;
        }

        x = Math.max(Math.min(x, limits.maxX), limits.minX);
        y = Math.max(Math.min(y, limits.maxY), limits.minY);

        drawLine(
          dragStartNode.x,
          dragStartNode.y,
          x,
          y,
          true,
          contentGroup,
          "1",
          "#000",
        );
      }
    }
  };

  return (
    <div style={{ position: "fixed" }}>
      <CustomDiv>
        <svg
          ref={svgRef}
          className="svgDualGraph"
          style={{ cursor: draw ? "crosshair" : "move" }}
          width={visualViewport?.width}
          height={visualViewport?.height}
          onClick={handleClick}
          onMouseMoveCapture={handleMouseMove}
        ></svg>

        <div
          style={{ position: "fixed", zIndex: 1000, right: "8px", top: "2px" }}
        ></div>
      </CustomDiv>

      <div
        style={{
          position: "fixed",
          zIndex: 1000,
          left: "50px",
          top: "100px",
          width: "100%",
          height: "20px",
          transition: "all .25s linear",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "50px",
              backgroundColor: "#FF9A56",
              borderRadius: "8px",
              border: "2px solid black",
              padding: "10px 17px",
              cursor: "pointer",
              transition: "opacity 0.3s",
              "&:hover": {
                opacity: 0.8,
              },
            }}
            onClick={navigateToInput}
          >
            <ArrowBackIcon htmlColor="black" sx={{ padding: "3px" }} />

            <Typography
              sx={{
                pl: "5px",
                color: "black",
                textAlign: "left",
                fontFamily: "Poppins",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "24px",
              }}
            >
              Back
            </Typography>
          </Box>
        </Box>
      </div>

      <TopNav svgRef={svgRef} />
      <Sidebar />
    </div>
  );
};

export default Graph;
